/**
 * restPresets
 * ===========
 *
 * Everything that makes the REST connector feel effortless:
 *
 *  1. REST_PRESETS - a curated catalog of popular data APIs (Alpaca,
 *     CoinGecko, Polygon, FRED, GitHub...). Each preset pre-fills the URL,
 *     the exact auth scheme the provider expects (incl. multi-header keys),
 *     pagination, and the incremental "time window" param that powers
 *     historical backfill + live sync. The user only pastes their keys.
 *
 *  2. Smart paste - parsers for `curl ...` commands and raw HTTP requests
 *     (the "GET /path HTTP/1.1 \n Header: value" format API docs love).
 *     Paste either into the URL box and we extract the URL, method, headers,
 *     auth and body automatically.
 *
 *  3. URL query-string helpers so the params editor and the URL field stay
 *     in sync (the URL is always the source of truth).
 */

// ---------------------------------------------------------------------------
// Provider presets
// ---------------------------------------------------------------------------

// Auth scaffold shapes (what the UI builds from `auth`):
//   { type: 'headers', headers: [{ name, label }] }       multiple secret headers
//   { type: 'apikey', in: 'header'|'query', name }        single API key
//   { type: 'bearer' }                                    Authorization: Bearer
//   { type: 'none' }                                      public API
//
// `keywords` feed the dropdown search (provider names, synonyms, tickers...).
export const REST_PRESETS = [
  // ---- Stocks & funds ------------------------------------------------------
  {
    id: 'alpaca-stock-bars',
    label: 'Alpaca · Stock bars',
    group: 'Stocks & funds',
    keywords: 'alpaca stocks equities bars ohlc candles minute trading',
    url: 'https://data.alpaca.markets/v2/stocks/bars?symbols=AAPL&timeframe=1Min&limit=10000&adjustment=raw&feed=sip&sort=asc',
    docs: 'https://docs.alpaca.markets/reference/stockbars',
    auth: {
      type: 'headers',
      headers: [
        { name: 'Apca-Api-Key-Id', label: 'API Key ID' },
        { name: 'Apca-Api-Secret-Key', label: 'API Secret Key' },
      ],
    },
    pagination: {
      type: 'cursor', cursor_param: 'page_token', cursor_path: 'next_page_token',
      page_size: 10000, max_pages: 10, backfill_max_pages: 200,
    },
    incremental: { param: 'start', hint: 'RFC-3339 (e.g. 2026-01-01T00:00:00Z)' },
    // Alpaca defaults start to "today", which is empty pre-market/after a
    // holiday - sample with a 7-day window so field detection always has rows.
    sampleWindowDays: 7,
    frequency: 'realtime',
    editHint: 'Change symbols= in the URL to your ticker. One symbol per source keeps the mapping clean.',
  },
  {
    id: 'polygon-aggs',
    label: 'Polygon · Stock aggregates',
    group: 'Stocks & funds',
    keywords: 'polygon stocks aggregates bars ohlc candles minute equities',
    url: 'https://api.polygon.io/v2/aggs/ticker/AAPL/range/1/minute/2026-01-01/2026-06-11?adjusted=true&sort=asc&limit=50000',
    docs: 'https://polygon.io/docs/stocks/get_v2_aggs_ticker__stocksticker__range__multiplier___timespan___from___to',
    auth: { type: 'apikey', in: 'query', name: 'apiKey' },
    pagination: null,
    incremental: null, // date range lives in the URL path on this endpoint
    frequency: '15m',
    editHint: 'Edit the ticker and the from/to dates in the URL path.',
  },
  {
    id: 'tiingo-daily',
    label: 'Tiingo · Daily prices',
    group: 'Stocks & funds',
    keywords: 'tiingo stocks daily eod prices close dividends splits',
    url: 'https://api.tiingo.com/tiingo/daily/AAPL/prices?startDate=2026-01-01&format=json',
    docs: 'https://www.tiingo.com/documentation/end-of-day',
    auth: { type: 'headers', headers: [{ name: 'Authorization', label: 'Authorization' }] },
    pagination: null,
    incremental: { param: 'startDate', hint: 'YYYY-MM-DD' },
    frequency: 'daily',
    editHint: 'Set the Authorization header value to "Token YOUR_API_KEY". Change the ticker in the URL path.',
  },
  {
    id: 'twelvedata-timeseries',
    label: 'Twelve Data · Time series',
    group: 'Stocks & funds',
    keywords: 'twelve data timeseries stocks forex crypto ohlc candles intraday',
    url: 'https://api.twelvedata.com/time_series?symbol=AAPL&interval=1min&outputsize=5000&order=ASC&format=JSON',
    docs: 'https://twelvedata.com/docs#time-series',
    auth: { type: 'apikey', in: 'query', name: 'apikey' },
    pagination: null,
    incremental: { param: 'start_date', hint: 'YYYY-MM-DD or YYYY-MM-DD hh:mm:ss' },
    frequency: 'realtime',
    editHint: 'Works for stocks, forex and crypto - change symbol= and interval= in the URL.',
  },
  {
    id: 'marketstack-eod',
    label: 'Marketstack · End-of-day prices',
    group: 'Stocks & funds',
    keywords: 'marketstack stocks eod end of day prices close',
    url: 'https://api.marketstack.com/v1/eod?symbols=AAPL&sort=ASC',
    docs: 'https://marketstack.com/documentation',
    auth: { type: 'apikey', in: 'query', name: 'access_key' },
    pagination: {
      type: 'offset', offset_param: 'offset', limit_param: 'limit',
      page_size: 1000, max_pages: 5, backfill_max_pages: 100,
    },
    incremental: { param: 'date_from', hint: 'YYYY-MM-DD' },
    frequency: 'daily',
    editHint: 'Change symbols= to your tickers (comma-separated works - rows carry a symbol field).',
  },
  {
    id: 'fmp-historical',
    label: 'FMP · Historical prices',
    group: 'Stocks & funds',
    keywords: 'financial modeling prep fmp historical prices daily stocks',
    url: 'https://financialmodelingprep.com/api/v3/historical-price-full/AAPL?serietype=line',
    docs: 'https://site.financialmodelingprep.com/developer/docs',
    auth: { type: 'apikey', in: 'query', name: 'apikey' },
    pagination: null,
    incremental: { param: 'from', hint: 'YYYY-MM-DD' },
    frequency: 'daily',
    editHint: 'Change the ticker in the URL path. Remove serietype=line for full OHLCV rows.',
  },
  {
    id: 'finnhub-news',
    label: 'Finnhub · Company news',
    group: 'Stocks & funds',
    keywords: 'finnhub news headlines company stock sentiment events',
    url: 'https://finnhub.io/api/v1/company-news?symbol=AAPL&from=2026-05-01&to=2026-06-11',
    docs: 'https://finnhub.io/docs/api/company-news',
    auth: { type: 'apikey', in: 'query', name: 'token' },
    pagination: null,
    incremental: null, // from/to range is required in the URL on this endpoint
    frequency: 'hourly',
    editHint: 'Change symbol= and the from/to dates. Tick "event" on the headline field to score it as an event.',
  },

  // ---- Crypto --------------------------------------------------------------
  {
    id: 'alpaca-crypto-bars',
    label: 'Alpaca · Crypto bars',
    group: 'Crypto',
    keywords: 'alpaca crypto bitcoin ethereum bars ohlc candles btc eth',
    url: 'https://data.alpaca.markets/v1beta3/crypto/us/bars?symbols=BTC/USD&timeframe=1Min&limit=10000&sort=asc',
    docs: 'https://docs.alpaca.markets/reference/cryptobars',
    auth: {
      type: 'headers',
      headers: [
        { name: 'Apca-Api-Key-Id', label: 'API Key ID' },
        { name: 'Apca-Api-Secret-Key', label: 'API Secret Key' },
      ],
    },
    pagination: {
      type: 'cursor', cursor_param: 'page_token', cursor_path: 'next_page_token',
      page_size: 10000, max_pages: 10, backfill_max_pages: 200,
    },
    incremental: { param: 'start', hint: 'RFC-3339 (e.g. 2026-01-01T00:00:00Z)' },
    sampleWindowDays: 7,
    frequency: 'realtime',
    editHint: 'Change symbols= in the URL to your pair (e.g. ETH/USD).',
  },
  {
    id: 'coingecko-markets',
    label: 'CoinGecko · Coin markets',
    group: 'Crypto',
    keywords: 'coingecko crypto bitcoin ethereum prices market cap volume',
    url: 'https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=bitcoin,ethereum&order=market_cap_desc',
    docs: 'https://docs.coingecko.com/reference/coins-markets',
    auth: { type: 'apikey', in: 'header', name: 'x-cg-demo-api-key', optional: true },
    pagination: null,
    incremental: null, // live snapshot endpoint - poll it for a live price series
    frequency: 'realtime',
    editHint: 'Live snapshot endpoint - we poll it on a schedule to build the series. Key optional on the free tier.',
  },
  {
    id: 'binance-tickers',
    label: 'Binance · 24h tickers',
    group: 'Crypto',
    keywords: 'binance crypto ticker price volume 24h public free btc eth',
    url: 'https://api.binance.com/api/v3/ticker/24hr?symbols=%5B%22BTCUSDT%22,%22ETHUSDT%22%5D',
    docs: 'https://developers.binance.com/docs/binance-spot-api-docs/rest-api/market-data-endpoints',
    auth: { type: 'none' },
    pagination: null,
    incremental: null, // live snapshot - poll for a live series
    frequency: 'realtime',
    editHint: 'Public API, no key needed. Edit the symbols list in the URL (it\'s a URL-encoded JSON array).',
  },
  {
    id: 'cryptocompare-histominute',
    label: 'CryptoCompare · Minute bars',
    group: 'Crypto',
    keywords: 'cryptocompare crypto minute bars ohlc history bitcoin',
    url: 'https://min-api.cryptocompare.com/data/v2/histominute?fsym=BTC&tsym=USD&limit=2000',
    docs: 'https://min-api.cryptocompare.com/documentation',
    auth: { type: 'apikey', in: 'query', name: 'api_key', optional: true },
    pagination: null,
    incremental: null, // pages backwards via toTs; we poll the latest window
    frequency: '15m',
    editHint: 'Change fsym=/tsym= for your pair. Key optional at low volume.',
  },
  {
    id: 'etherscan-txlist',
    label: 'Etherscan · Wallet transactions',
    group: 'Crypto',
    keywords: 'etherscan ethereum wallet transactions onchain blockchain address',
    url: 'https://api.etherscan.io/api?module=account&action=txlist&address=0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe&startblock=0&endblock=latest&sort=asc',
    docs: 'https://docs.etherscan.io/api-endpoints/accounts',
    auth: { type: 'apikey', in: 'query', name: 'apikey' },
    pagination: {
      type: 'page', page_param: 'page', page_size_param: 'offset',
      page_size: 1000, max_pages: 10, backfill_max_pages: 100,
    },
    incremental: null, // block-numbered, not date-windowed; adjust startblock in the URL
    frequency: '15m',
    editHint: 'Change address= to the wallet you want to track. Raise startblock= to skip old history.',
  },

  // ---- Economics & energy --------------------------------------------------
  {
    id: 'fred-observations',
    label: 'FRED · Series observations',
    group: 'Economics & energy',
    keywords: 'fred federal reserve gdp cpi unemployment inflation rates macro economics',
    url: 'https://api.stlouisfed.org/fred/series/observations?series_id=GDP&file_type=json&sort_order=asc',
    docs: 'https://fred.stlouisfed.org/docs/api/fred/series_observations.html',
    auth: { type: 'apikey', in: 'query', name: 'api_key' },
    pagination: {
      type: 'offset', offset_param: 'offset', limit_param: 'limit',
      page_size: 1000, max_pages: 10, backfill_max_pages: 200,
    },
    incremental: { param: 'observation_start', hint: 'YYYY-MM-DD' },
    frequency: 'daily',
    editHint: 'Change series_id= to any FRED series (GDP, CPIAUCSL, UNRATE...).',
  },
  {
    id: 'eia-electricity',
    label: 'EIA · Electricity demand',
    group: 'Economics & energy',
    keywords: 'eia energy electricity demand grid power hourly utilities',
    url: 'https://api.eia.gov/v2/electricity/rto/region-data/data/?frequency=hourly&data[0]=value&facets[respondent][]=US48&sort[0][column]=period&sort[0][direction]=asc',
    docs: 'https://www.eia.gov/opendata/documentation.php',
    auth: { type: 'apikey', in: 'query', name: 'api_key' },
    pagination: {
      type: 'offset', offset_param: 'offset', limit_param: 'length',
      page_size: 5000, max_pages: 5, backfill_max_pages: 100,
    },
    incremental: { param: 'start', hint: 'YYYY-MM-DDThh (matches the series frequency)' },
    frequency: 'hourly',
    editHint: 'US grid demand by default - change the facets in the URL for other regions/series.',
  },
  {
    id: 'nasa-flares',
    label: 'NASA · Solar flares (DONKI)',
    group: 'Economics & energy',
    keywords: 'nasa space weather solar flares donki geomagnetic',
    url: 'https://api.nasa.gov/DONKI/FLR?startDate=2026-05-01',
    docs: 'https://api.nasa.gov/',
    auth: { type: 'apikey', in: 'query', name: 'api_key' },
    pagination: null,
    incremental: { param: 'startDate', hint: 'YYYY-MM-DD' },
    frequency: 'hourly',
    editHint: 'DEMO_KEY works for light use. Other DONKI feeds (CME, GST...) swap the path segment.',
  },

  // ---- Weather & environment ------------------------------------------------
  {
    id: 'visualcrossing-timeline',
    label: 'Visual Crossing · Weather timeline',
    group: 'Weather & environment',
    keywords: 'visual crossing weather temperature rain wind forecast history daily',
    url: 'https://weather.visualcrossing.com/VisualCrossingWebServices/rest/services/timeline/London?unitGroup=metric&include=days&contentType=json',
    docs: 'https://www.visualcrossing.com/resources/documentation/weather-api/timeline-weather-api/',
    auth: { type: 'apikey', in: 'query', name: 'key' },
    pagination: null,
    incremental: null, // date range goes in the URL path (/London/2026-01-01/2026-06-11)
    frequency: 'hourly',
    editHint: 'Change the location in the URL path. Append /2026-01-01/2026-06-11 to the path for history.',
  },
  {
    id: 'weatherbit-hourly',
    label: 'Weatherbit · Hourly forecast',
    group: 'Weather & environment',
    keywords: 'weatherbit weather hourly forecast temperature precipitation',
    url: 'https://api.weatherbit.io/v2.0/forecast/hourly?city=London&hours=120',
    docs: 'https://www.weatherbit.io/api/weather-forecast-120-hour',
    auth: { type: 'apikey', in: 'query', name: 'key' },
    pagination: null,
    incremental: null, // forecast feed - poll it live
    frequency: 'hourly',
    editHint: 'Change city= (or use lat=/lon=). Each poll refreshes the rolling 120-hour forecast.',
  },

  // ---- News & events ---------------------------------------------------------
  {
    id: 'newsapi-everything',
    label: 'NewsAPI · Everything search',
    group: 'News & events',
    keywords: 'newsapi news articles headlines media search keywords',
    url: 'https://newsapi.org/v2/everything?q=bitcoin&sortBy=publishedAt&language=en',
    docs: 'https://newsapi.org/docs/endpoints/everything',
    auth: { type: 'apikey', in: 'header', name: 'X-Api-Key' },
    pagination: {
      type: 'page', page_param: 'page', page_size_param: 'pageSize',
      page_size: 100, max_pages: 1, backfill_max_pages: 5,
    },
    incremental: { param: 'from', hint: 'ISO date or datetime' },
    frequency: '15m',
    editHint: 'Change q= to your topic. Tick "event" on the title field to score headlines as events.',
  },
  {
    id: 'github-events',
    label: 'GitHub · Repo events',
    group: 'News & events',
    keywords: 'github repo events commits issues activity developer',
    url: 'https://api.github.com/repos/facebook/react/events?per_page=100',
    docs: 'https://docs.github.com/en/rest/activity/events',
    auth: { type: 'bearer', optional: true },
    pagination: { type: 'link', page_size: 100, max_pages: 3, backfill_max_pages: 10 },
    incremental: null,
    frequency: '15m',
    editHint: 'Point the URL at your own owner/repo. Token optional for public repos (higher rate limits with one).',
  },

  // ---- Commerce & ops ---------------------------------------------------------
  {
    id: 'shopify-orders',
    label: 'Shopify · Orders',
    group: 'Commerce & ops',
    keywords: 'shopify orders ecommerce sales revenue store commerce',
    url: 'https://YOUR-STORE.myshopify.com/admin/api/2024-10/orders.json?status=any&limit=250',
    docs: 'https://shopify.dev/docs/api/admin-rest/2024-10/resources/order',
    auth: { type: 'headers', headers: [{ name: 'X-Shopify-Access-Token', label: 'Admin access token' }] },
    pagination: { type: 'link', page_size: 250, max_pages: 4, backfill_max_pages: 100 },
    incremental: { param: 'updated_at_min', hint: 'ISO 8601' },
    frequency: '15m',
    editHint: 'Replace YOUR-STORE in the URL with your shop subdomain.',
  },
];

export const presetById = (id) => REST_PRESETS.find((p) => p.id === id) || null;

// ---------------------------------------------------------------------------
// Smart paste - cURL + raw-HTTP request parsing
// ---------------------------------------------------------------------------

/** Tokenize a shell-ish command respecting single/double quotes. */
const shellTokens = (text) => {
  const tokens = [];
  let cur = '';
  let quote = null;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quote) {
      if (ch === quote) { quote = null; } else { cur += ch; }
    } else if (ch === "'" || ch === '"') {
      quote = ch;
    } else if (ch === '\\' && text[i + 1] === '\n') {
      i += 1; // line continuation
    } else if (/\s/.test(ch)) {
      if (cur) { tokens.push(cur); cur = ''; }
    } else {
      cur += ch;
    }
  }
  if (cur) tokens.push(cur);
  return tokens;
};

/**
 * Parse a `curl ...` command into { url, method, headers, body, username,
 * password }. Handles -X/-H/-u/-d (+ long forms) and the bare URL.
 */
export function parseCurl(text) {
  const tokens = shellTokens(text.trim());
  if (!tokens.length || !/^curl$/i.test(tokens[0])) return null;

  const req = { url: '', method: '', headers: [], body: '', username: '', password: '' };
  let i = 1;
  const next = () => tokens[++i];

  while (i < tokens.length) {
    const t = tokens[i];
    if (t === '-X' || t === '--request') {
      req.method = (next() || '').toUpperCase();
    } else if (t === '-H' || t === '--header') {
      const h = next() || '';
      const idx = h.indexOf(':');
      if (idx > 0) req.headers.push({ name: h.slice(0, idx).trim(), value: h.slice(idx + 1).trim() });
    } else if (t === '-u' || t === '--user') {
      const u = next() || '';
      const idx = u.indexOf(':');
      req.username = idx >= 0 ? u.slice(0, idx) : u;
      req.password = idx >= 0 ? u.slice(idx + 1) : '';
    } else if (t === '-d' || t === '--data' || t === '--data-raw' || t === '--data-binary' || t === '--data-urlencode') {
      req.body = next() || '';
      if (!req.method) req.method = 'POST';
    } else if (t === '--url') {
      req.url = next() || '';
    } else if (t === '--compressed' || t === '-s' || t === '-L' || t === '-k' || t === '--insecure' || t === '-G' || t === '--get') {
      // flags with no value we can safely ignore
    } else if (!t.startsWith('-') && !req.url && /^https?:\/\//i.test(t)) {
      req.url = t;
    }
    i += 1;
  }
  if (!req.url) return null;
  if (!req.method) req.method = 'GET';
  return req;
}

/**
 * Parse a raw HTTP request (the format API docs + HTTP clients show):
 *
 *   GET /v2/stocks/bars?symbols=AAPL&timeframe=1Min HTTP/1.1
 *   Accept: application/json
 *   Apca-Api-Key-Id: XXXX
 *   Host: data.alpaca.markets
 *
 * Returns the same shape as parseCurl. The Host header becomes the URL host
 * (https assumed - every data API worth using is TLS).
 */
export function parseRawHttp(text) {
  const lines = text.split(/\r?\n/);
  const first = (lines[0] || '').trim();
  const m = first.match(/^(GET|POST|PUT|PATCH|DELETE|HEAD)\s+(\S+)(?:\s+HTTP\/[\d.]+)?$/i);
  if (!m) return null;

  const req = { url: '', method: m[1].toUpperCase(), headers: [], body: '', username: '', password: '' };
  const path = m[2];
  let host = '';
  let bodyStart = -1;

  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i];
    if (!line.trim()) { bodyStart = i + 1; break; }
    const idx = line.indexOf(':');
    if (idx <= 0) continue;
    const name = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (/^host$/i.test(name)) host = value;
    else req.headers.push({ name, value });
  }
  if (bodyStart >= 0) req.body = lines.slice(bodyStart).join('\n').trim();

  if (/^https?:\/\//i.test(path)) req.url = path;          // absolute-form request line
  else if (host) req.url = `https://${host}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!req.url) return null;
  return req;
}

/** Detect + parse either supported paste format. */
export function detectSmartPaste(text) {
  const t = (text || '').trim();
  if (!t) return null;
  if (/^curl(\s|$)/i.test(t)) {
    const req = parseCurl(t);
    return req ? { kind: 'curl', request: req } : null;
  }
  if (/^(GET|POST|PUT|PATCH|DELETE|HEAD)\s+\S+/i.test(t) && /\n/.test(t)) {
    const req = parseRawHttp(t);
    return req ? { kind: 'http', request: req } : null;
  }
  return null;
}

// Header names that are transport noise, not API config.
const NOISE_HEADERS = /^(host|content-length|connection|accept-encoding|user-agent|cache-control|pragma)$/i;
const SECRETISH = /(key|token|secret|auth|signature|session)/i;

/**
 * Classify parsed headers into an auth scheme + leftover custom headers.
 *
 *   Authorization: Bearer X  -> bearer
 *   Authorization: Basic b64 -> basic (decoded)
 *   1+ secret-looking headers -> 'headers' auth (multi-header keys, the
 *                                Alpaca case)
 *   everything else           -> plain custom headers
 */
export function classifyAuth(headers, { username, password } = {}) {
  const out = { auth_type: 'none', token: '', username: username || '', password: password || '', auth_headers: [], custom: [] };

  headers.forEach((h) => {
    if (NOISE_HEADERS.test(h.name)) return;
    if (/^authorization$/i.test(h.name)) {
      const bearer = h.value.match(/^Bearer\s+(.+)$/i);
      const basic = h.value.match(/^Basic\s+(.+)$/i);
      if (bearer) { out.auth_type = 'bearer'; out.token = bearer[1]; return; }
      if (basic) {
        try {
          const [u, ...rest] = atob(basic[1]).split(':');
          out.auth_type = 'basic'; out.username = u; out.password = rest.join(':');
          return;
        } catch (e) { /* fall through to header auth */ }
      }
      out.auth_headers.push(h);
      return;
    }
    if (SECRETISH.test(h.name)) out.auth_headers.push(h);
    else out.custom.push(h);
  });

  if (out.auth_type === 'none' && out.username) out.auth_type = 'basic';
  if (out.auth_type === 'none' && out.auth_headers.length) out.auth_type = 'headers';
  return out;
}

// ---------------------------------------------------------------------------
// URL <-> query-param helpers (the URL field is the source of truth)
// ---------------------------------------------------------------------------

export function getUrlParams(url) {
  try {
    const u = new URL(url);
    return Array.from(u.searchParams.entries()).map(([key, value]) => ({ key, value }));
  } catch (e) {
    return [];
  }
}

export function setUrlParams(url, rows) {
  try {
    const u = new URL(url);
    u.search = '';
    rows.forEach(({ key, value }) => { if (key) u.searchParams.append(key, value ?? ''); });
    return u.toString();
  } catch (e) {
    return url;
  }
}

/** Default "backfill from" - 30 days back, date-only ISO (APIs accept both). */
export function defaultBackfillStart() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}
